<template>
    <Head :title="seo.title || undefined">
        <meta
            v-if="seo.description"
            head-key="description"
            name="description"
            :content="seo.description"
        />
        <link
            v-if="seo.canonical"
            head-key="canonical"
            rel="canonical"
            :href="seo.canonical"
        />
        <meta
            v-for="(value, key) in properties"
            :key="key"
            :head-key="key"
            :property="key"
            :content="value"
        />
        <meta
            v-for="(value, key) in metadata"
            :key="key"
            :head-key="key"
            :name="key"
            :content="value"
        />
        <component
            :is="jsonLdTag(block, index)"
            v-for="(block, index) in seo.jsonLd"
            :key="index"
        />
    </Head>
</template>
<script setup lang="ts">
import { Head } from '@inertiajs/vue3';
import { computed, h } from 'vue';
import type { SeoData } from '@/types/blog';
const props = defineProps<{ seo: SeoData }>();
const properties = computed(() => ({
    'og:type': props.seo.type,
    'og:site_name': props.seo.siteName,
    'og:locale': props.seo.locale,
    ...(props.seo.title ? { 'og:title': props.seo.title } : {}),
    ...(props.seo.description
        ? { 'og:description': props.seo.description }
        : {}),
    ...(props.seo.canonical ? { 'og:url': props.seo.canonical } : {}),
    ...(props.seo.image ? { 'og:image': props.seo.image } : {}),
    ...(props.seo.imageWidth
        ? { 'og:image:width': String(props.seo.imageWidth) }
        : {}),
    ...(props.seo.imageHeight
        ? { 'og:image:height': String(props.seo.imageHeight) }
        : {}),
    ...props.seo.properties,
}));
const metadata = computed(() => ({
    'twitter:card': props.seo.image ? 'summary_large_image' : 'summary',
    ...(props.seo.title ? { 'twitter:title': props.seo.title } : {}),
    ...(props.seo.description
        ? { 'twitter:description': props.seo.description }
        : {}),
    ...(props.seo.image ? { 'twitter:image': props.seo.image } : {}),
    ...(props.seo.twitterSite ? { 'twitter:site': props.seo.twitterSite } : {}),
    ...props.seo.meta,
}));
function jsonLdTag(block: Record<string, unknown>, index: number) {
    return () =>
        h(
            'script',
            {
                'head-key': `jsonld-${index}`,
                type: 'application/ld+json',
            },
            serialize(block),
        );
}
function serialize(block: Record<string, unknown>): string {
    return JSON.stringify(block).replace(/</g, '\\u003c');
}
</script>
